import * as React from 'react';
import { useEffect, useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';

import analyticsWrapper from 'utility/analytics-wrapper';
import { useStores } from 'utility/hooks';
import { trackAnalytics } from 'utility';
import { TertiaryButtonTracked } from 'components/shared/tracked';
import { TripTabs } from '..';
import { FlightsSearchFormTravelersOption } from 'types/search/wizard';
import { LocalizedInteractableFieldComponentProps } from 'src/types/shared';
import { SearchFormTrigger, SortFilterTrigger } from '.';

interface PlaybackProps extends LocalizedInteractableFieldComponentProps {
  searchFormHeadingRef: React.MutableRefObject<HTMLHeadingElement>;
}

let shouldSaveChanges = false;
let shouldFocusOnTrigger = false;

export const Playback = observer(({ searchFormHeadingRef, onFieldInteraction }: PlaybackProps) => {
  const { routeStore, searchCriteriaStore, searchFormStore, sortFilterStore, context, analytics } = useStores();
  const searchFormTriggerRef = useRef<HTMLInputElement>(null);
  const [searchFormExpanded, setSearchFormExpanded] = useState(false);
  const [shouldOpenSearchFormOnError, setShouldOpenSearchFormOnError] = useState(true);
  const [shouldCloseSearchFormOnSearchLoading, setShouldCloseSearchFormOnSearchLoading] = useState(false);
  const { searchForm, essEndpoint } = searchFormStore;
  const { expandFormAnalytics, collapseFormAnalytics, closeActionAccessibility, closeFormAction } = searchForm;

  const openSearchForm = useCallback(() => {
    setSearchFormExpanded(true);
    trackAnalytics(expandFormAnalytics, analytics);
    onFieldInteraction && onFieldInteraction();
    shouldSaveChanges = false;
    if (!searchFormStore.loading) {
      setShouldCloseSearchFormOnSearchLoading(true);
    }
  }, [analytics, expandFormAnalytics, onFieldInteraction, searchFormStore.loading]);

  const closeSearchForm = useCallback(() => {
    setSearchFormExpanded(false);
    setShouldOpenSearchFormOnError(true);
    setShouldCloseSearchFormOnSearchLoading(false);

    if (shouldSaveChanges) {
      searchCriteriaStore.syncWithSearchFormStore(searchFormStore);
      searchCriteriaStore.clearSortFilterSearchCriteria();
      searchCriteriaStore.triggerSearch(routeStore, context);
      sortFilterStore.loading = true;
      analytics.resetAnalyticsForNewPage();
    } else {
      if (searchFormStore.loading) {
        searchFormStore.syncWithSearchCriteriaStore(searchCriteriaStore);
      } else {
        searchFormStore.restoreSearchFormToInitialState();
      }
      trackAnalytics(collapseFormAnalytics, analytics);
    }
  }, [
    collapseFormAnalytics,
    context,
    routeStore,
    searchCriteriaStore,
    sortFilterStore.loading,
    searchFormStore,
    analytics
  ]);

  const verifyTravelersErrors = useCallback(() => {
    const travelerOption = searchForm.searchOptions.find(
      option => option.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption;

    if (travelerOption.currentErrors && travelerOption.currentErrors.length > 0) {
      const travelersDialogQuery = routeStore.mergeQueryString(routeStore.search, {
        pwaDialog: 'FlightsSearchFormTravelersOption-dialog'
      });
      routeStore.updateSearch(travelersDialogQuery);
    }
  }, [routeStore, searchForm.searchOptions]);

  const submitChanges = useCallback(() => {
    const valid = searchFormStore.validate(analytics);

    searchFormStore.clearErrorRef();

    if (!valid) {
      verifyTravelersErrors();

      return;
    }

    shouldSaveChanges = true;
    sortFilterStore.clearFilters();
    analyticsWrapper.resetAnalyticsPayLoad(analytics);
    closeSearchForm();
  }, [analytics, closeSearchForm, searchFormStore, sortFilterStore, verifyTravelersErrors]);

  const validateSearchFormLocation = useCallback(() => {
    let valid = true;

    searchForm.journeys.forEach(journey => {
      const { destinationLocation, originLocation } = journey;
      valid = valid && !destinationLocation.currentError && !originLocation.currentError;
    });

    return valid;
  }, [searchForm.journeys]);

  useEffect(() => {
    if (shouldCloseSearchFormOnSearchLoading && searchFormStore.loading) {
      closeSearchForm();
    } else if (!searchFormExpanded && shouldOpenSearchFormOnError && !validateSearchFormLocation()) {
      openSearchForm();
      setShouldOpenSearchFormOnError(false);
    }
  }, [
    openSearchForm,
    closeSearchForm,
    searchFormExpanded,
    shouldCloseSearchFormOnSearchLoading,
    shouldOpenSearchFormOnError,
    validateSearchFormLocation,
    searchFormStore.loading
  ]);

  useEffect(() => {
    if (searchFormExpanded && searchFormHeadingRef.current) {
      searchFormHeadingRef.current.focus();
      shouldFocusOnTrigger = true;
    } else if (shouldFocusOnTrigger && !searchFormExpanded && searchFormTriggerRef.current) {
      searchFormTriggerRef.current.focus();
    }
  }, [searchFormExpanded, searchFormHeadingRef, searchFormStore.loading]);

  return (
    <>
      {searchFormExpanded ? (
        <div data-test-id="playback">
          <TripTabs essEndpoint={essEndpoint} onFieldInteraction={onFieldInteraction} />
          <UitkLayoutGrid columns={{ medium: 6, small: 1 }}>
            <UitkLayoutGridItem colSpan={{ medium: 2, small: 1 }}>
              <UitkPrimaryButton
                onClick={submitChanges}
                isFullWidth={true}
                size={UitkButtonSize.LARGE}
                text={searchForm.searchAction}
              />
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
          <TertiaryButtonTracked
            onClick={closeSearchForm}
            isFullWidth={true}
            size={UitkButtonSize.LARGE}
            text={closeFormAction && closeFormAction.displayAction}
            aria-label={closeActionAccessibility}
            {...(closeFormAction && closeFormAction.analytics)}
          />
        </div>
      ) : (
        <UitkLayoutFlex space="three" data-test-id="playback">
          <UitkLayoutFlexItem grow={1}>
            <div>
              <SearchFormTrigger searchFormTriggerRef={searchFormTriggerRef} openSearchForm={openSearchForm} />
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem grow={0}>
            <div>
              <SortFilterTrigger />
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      )}
    </>
  );
});
